import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { recurrenceSchema, scheduleNextField, type Recurrence, type ScheduleNext } from './types';
import { readResponse } from 'api/shared/readResponse';

// GET /recurrence_definitions/:zone_name/:name — the single-record read
// (full detail). Backend: `RecurrenceDefinitionService.getByZoneAndName`.
//
// This is its OWN type, distinct from the list row (`RecurrenceDefinitionRow`)
// and the write responses (`Created`/`Updated`), even where the fields match
// today: those come from separate backend queries (equal-by-coincidence, not
// identity — see `../../README.md`). The shared sub-objects `recurrences` /
// `folder_path` come from `./types`.
export interface RecurrenceDefinition {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string | null;
  // When false, failed scheduled node instances referencing this definition are
  // not automatically resubmitted (only applies when schedule_next is on_complete).
  readonly resubmit_on_error: boolean;
  // When to submit the next occurrence. `on_start` creates it as soon as the
  // instance becomes active; `resubmit_on_error` is then ignored for successors.
  readonly schedule_next: ScheduleNext;
  // One-to-many: a definition owns one or more recurrence rules, ordered by
  // `sort_order` (the backend returns them sorted). Always non-empty for a saved
  // definition.
  readonly recurrences: readonly Recurrence[];
  // Folder support (nap-backend #1076). `folder_id` is the assigned folder (or
  // null); `folder_path` is its resolved root→leaf path for display.
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

const recurrenceDefinitionSchema = object<RecurrenceDefinition>({
  recurrence_definition_id: num(REJECT),
  name: str(),
  description: nullable(str()),
  zone_name: nullable(str()),
  resubmit_on_error: bool(),
  schedule_next: scheduleNextField,
  // `REJECT` because this record is loaded into an editor that saves the WHOLE thing
  // back, and its dialog does not yet refuse a degraded answer. Without it a
  // non-array here reads as "no recurrences", opens the
  // dialog on that, and the next save persists it. It does NOT cover a single
  // unreadable recurrence: that row is dropped and the dialog still opens, which is
  // the same clobber one layer down and needs this read to return `Received`.
  recurrences: array(recurrenceSchema, REJECT),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  created_at: nullable(str()),
  updated_at: nullable(str()),
});

/**
 * Parse a single-record `recurrence_definitions` response. Throws when it cannot
 * be read: structurally, or because the identity had to be substituted for.
 * Drift elsewhere logs a warning and substitutes defaults.
 */
export const parseRecurrenceDefinition = (raw: unknown, source: string): Received<RecurrenceDefinition> => {
  return readResponse(raw, { schema: recurrenceDefinitionSchema, source });
};

export const getRecurrenceDefinition = async (zone_name: string, name: string): Promise<Received<RecurrenceDefinition>> => {
  const res = await api.get(`/recurrence_definitions/${encodeURIComponent(zone_name)}/${encodeURIComponent(name)}`);
  return parseRecurrenceDefinition(res.data, 'getRecurrenceDefinition');
};
